import { memo, useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useNavigate, useParams } from "react-router-dom";

import Clarity from "@microsoft/clarity";
import { useDispatch, useSelector } from "react-redux";
import { LexIcon } from "../../../../assets/icons/lex";
import { getDoctrineById } from "../../../services/get-doctrine-by-id";
import { getQueryValue } from "../../../store/selectors";
import {
  getHighlightedExcerpts,
  handleClickNote,
  handleInternalSpaNavigation,
  printContent,
  processDocumentText,
} from "../../../utils";
import { saveDocumentWithNotify } from "../../../utils/_handleSaveDocument";
import { Pagewrapper } from "../../components";
import { PDLTypography } from "../../components/dna";
import LexLoading from "../../components/global/_lexLoading";
import NotesModal from "../../components/global/_notesModal";
import DoctrineDetailsHeaderActions from "./components/DoctrineDetailsHeaderActions";
import "./index.scss";

const HtmlContent = memo(({ html }) => (
  <div className="content-text" dangerouslySetInnerHTML={{ __html: html }} />
));

const DoctrineDetails = (props) => {
  const { id } = useParams();

  const [doctrine, setDoctrine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent] = useState("");
  const [noteTitle] = useState("");
  const [modalType, setModalType] = useState("note");
  const [articleId, setArticleId] = useState("");
  const [articleUrl, setArticleUrl] = useState("");

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const query = useSelector(getQueryValue);

  const contentHtml = useMemo(() => {
    if (!doctrine?.texto) return "";
    return query
      ? getHighlightedExcerpts(
          doctrine.texto,
          query,
          doctrine.texto.length,
          false,
        )
      : doctrine.texto;
  }, [doctrine?.texto, query]);

  const handlePrint = () =>
    printContent({
      text: doctrine?.texto,
      title: doctrine?.titulo,
    });

  const handleOpenFindBar = () => {
    window.dispatchEvent(new CustomEvent("pdl:openFindBar"));
  };

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: doctrine?._id,
      id: doctrine?.id,
      tipo: "Doutrina",
      url: window.location.href,
    });

  useEffect(() => {
    const loadDoctrine = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getDoctrineById(id);

        if (response && response.texto) {
          response.texto = processDocumentText({ htmlText: response.texto });
        }

        setDoctrine(response);
      } catch (err) {
        const status = err?.response?.status;

        setError(
          status === 404
            ? "Estamos trabalhando para disponibilizá-lo em breve."
            : err?.response?.data?.message ||
                "Não foi possível encontrar a doutrina",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadDoctrine();
    }
  }, [id]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const rawTarget = event.target;

      const target =
        rawTarget.closest?.("[data-nota-link], .nota-super, a") || rawTarget;

      if (showNoteModal) return;

      if (target.classList?.contains("nota-super")) {
        handleClickNote(event);
        return;
      }

      if (target.tagName?.toLowerCase() === "a") {
        const href = target.getAttribute("href") || "";

        const url = new URL(href, window.location.origin);

        const artIdFromQuery = url.searchParams.get("artId");

        const match = href.match(/(art\d+)/i);

        const articlePart = artIdFromQuery || match?.[1];

        if (articlePart) {
          event.preventDefault();

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];
          cleaned = cleaned.split("?")[0];

          if (!artIdFromQuery) {
            if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
              cleaned = cleaned.replace(/art\d+/i, "") + articlePart;
            }
          } else {
            cleaned = articlePart;
          }

          setArticleId(cleaned);
          setArticleUrl(href);
          setModalType("legislation");
          setShowNoteModal(true);

          return;
        } else {
          if (handleInternalSpaNavigation(href, event, navigate)) {
            return;
          }
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [doctrine, showNoteModal]);

  useEffect(() => {
    if (!doctrine) return;

    Clarity.event("doctrine_view");

    Clarity.setTag("doc_type", "doutrina");
    Clarity.setTag("doc_id", doctrine?.id);
    Clarity.setTag("doc_name", doctrine?.titulo);
  }, [doctrine]);

  if (loading) {
    return (
      <Pagewrapper {...props} showHeader headerClass="details-sticky">
        <Container>
          <Row>
            <Col>
              <div className="content-loading">
                <LexLoading customText="Buscando doutrina..." />
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  if (error) {
    return (
      <Pagewrapper {...props} showHeader headerClass="details-sticky">
        <Container>
          <Row>
            <Col>
              <div className="error-section text-center">
                <LexIcon />
                <PDLTypography size="xl" variant="primary" weight="medium">
                  Este documento ainda não está disponível.
                </PDLTypography>
                <p className="text-muted">{error}</p>
                <p className="text-muted">
                  Se for urgente, fale com a gente. Teremos prazer em ajudar.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  return (
    <Pagewrapper {...props} showHeader headerClass="details-sticky" key={id}>
      <Container>
        <Row>
          <Col>
            <DoctrineDetailsHeaderActions
              title={doctrine?.titulo || "Doutrina"}
              isSavingDocument={isSavingDocument}
              onOpenFindBar={handleOpenFindBar}
              onSaveDocument={handleSaveDocument}
              onPrint={handlePrint}
            />

            <div className="mt-3">
              <div className="content-box">
                {doctrine?.texto ? (
                  <HtmlContent html={contentHtml} />
                ) : (
                  <p className="text-muted">Conteúdo não disponível.</p>
                )}
              </div>
            </div>
          </Col>
          <NotesModal
            noteContent={noteContent}
            noteTitle={noteTitle}
            setShowNoteModal={setShowNoteModal}
            showNoteModal={showNoteModal}
            type={modalType}
            articleId={articleId}
            url={articleUrl}
          />
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default DoctrineDetails;
